import { Vector3 } from "three";
import type { CenterlineData } from "./useTunnelCenterline";
import { type ProfileAssignment, getProfileAtStation } from "./tunnelProfileShapes";
import { storageKey } from "./projectConfig";

/**
 * A point in tunnel-local coordinates.
 * This is the canonical representation for data drawn on the utbrettsprofil.
 *
 * - station:   distance along the tunnel centerline (meters)
 * - perimeter: distance along the cross-section profile outline (meters),
 *              measured from the bottom-left corner (road surface) going
 *              clockwise up the left wall, over the arch, and down the right wall.
 */
export interface TunnelPoint {
  station: number;
  perimeter: number;
}

/**
 * A line drawn on the utbrettsprofil, stored in tunnel-local coordinates.
 */
export interface WeaknessZoneLine {
  id: string;
  label?: string;
  points: TunnelPoint[];
  color: string;
  createdAt: number;
}

const LEGACY_STORAGE_KEY = "e16-foa-deom:weakness-zone-lines";

function linesKey(projectId: string): string {
  return storageKey(projectId, "weakness-zone-lines");
}

function migrateLegacy(projectId: string): void {
  const key = linesKey(projectId);
  if (localStorage.getItem(key)) return;
  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacy) localStorage.setItem(key, legacy);
}

/** Load lines from localStorage */
export function loadLines(projectId: string): WeaknessZoneLine[] {
  try {
    migrateLegacy(projectId);
    const raw = localStorage.getItem(linesKey(projectId));
    if (!raw) return [];
    return JSON.parse(raw) as WeaknessZoneLine[];
  } catch {
    return [];
  }
}

/** Save lines to localStorage */
export function saveLines(projectId: string, lines: WeaknessZoneLine[]): void {
  localStorage.setItem(linesKey(projectId), JSON.stringify(lines));
}

/**
 * Project a tunnel-local point to 3D world coordinates.
 *
 * Uses the centerline curve + Frenet frames to find the 3D position:
 * 1. station -> point and frame on the centerline
 * 2. perimeter -> walk along the 2D profile outline to find the local (x, y) offset
 * 3. Map local offset to 3D using the Frenet binormal and normal
 */
export function tunnelPointTo3D(
  pt: TunnelPoint,
  data: CenterlineData,
  profileAssignments: ProfileAssignment[],
  frames?: { normals: Vector3[]; binormals: Vector3[] },
  curvePoints?: Vector3[],
  frameSamples?: number,
): Vector3 {
  const samples = frameSamples ?? 500;
  const t = Math.max(0, Math.min(1, pt.station / data.totalLength));
  const frameIdx = Math.min(Math.round(t * samples), samples);

  // Get curve point and frame vectors
  const center = curvePoints
    ? curvePoints[frameIdx]
    : data.curve.getPointAt(t);

  const computedFrames = frames ?? data.curve.computeFrenetFrames(samples, false);
  const normal = computedFrames.normals[frameIdx];
  const binormal = computedFrames.binormals[frameIdx];

  // Get the profile at this station and walk along its perimeter
  const profile = getProfileAtStation(pt.station, profileAssignments);
  const pts2d = profile.points;

  // Walk along the profile perimeter to find the 2D offset at the given perimeter distance
  let remaining = pt.perimeter;
  let localX = pts2d[0].x;
  let localY = pts2d[0].y;

  for (let i = 1; i < pts2d.length; i++) {
    const dx = pts2d[i].x - pts2d[i - 1].x;
    const dy = pts2d[i].y - pts2d[i - 1].y;
    const segLen = Math.sqrt(dx * dx + dy * dy);

    if (remaining <= segLen) {
      const frac = segLen > 0 ? remaining / segLen : 0;
      localX = pts2d[i - 1].x + dx * frac;
      localY = pts2d[i - 1].y + dy * frac;
      break;
    }
    remaining -= segLen;
    localX = pts2d[i].x;
    localY = pts2d[i].y;
  }

  // Map to 3D: profile X -> binormal, profile Y -> -normal (same as TunnelProfiles.tsx)
  return new Vector3(
    center.x + localX * binormal.x - localY * normal.x,
    center.y + localX * binormal.y - localY * normal.y,
    center.z + localX * binormal.z - localY * normal.z,
  );
}

/**
 * Project an entire line to 3D. Returns array of Vector3.
 * Accepts pre-computed frames for efficiency when projecting many lines.
 */
export function lineToWorld3D(
  line: WeaknessZoneLine,
  data: CenterlineData,
  profileAssignments: ProfileAssignment[],
  frames?: { normals: Vector3[]; binormals: Vector3[] },
  curvePoints?: Vector3[],
  frameSamples?: number,
): Vector3[] {
  return line.points.map((pt) =>
    tunnelPointTo3D(pt, data, profileAssignments, frames, curvePoints, frameSamples),
  );
}
